import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, message, Spin, Switch, Space, Breadcrumb, Divider, Tooltip, Select, Tag } from 'antd';
import { 
  ArrowLeftOutlined, 
  SaveOutlined, 
  ProjectOutlined, 
  CodeOutlined, 
  GlobalOutlined, 
  KeyOutlined, 
  LockOutlined,
  QuestionCircleOutlined,
  ToolOutlined
} from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { createProject, updateProject, getProjectById, Project, getFormConfigById } from '../../services/api';

import axios from 'axios';
import './styles.css';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface ProtocolProjectFormProps {
  mode: 'create' | 'edit';
}

interface FormField {
  key: string;
  label: string;
  type: string;
  required?: boolean;
  options?: Array<{ label: string; value: string | number }>;
  placeholder?: string;
  [key: string]: any;
}

interface FormConfig {
  id: string;
  name: string;
  fields: FormField[];
}

// Define API response interface
interface ApiResponse<T> {
  code: number;
  msg?: string;
  data?: T;
}

// Define the getFormConfigById function inline
export const getFormConfigByIdFetch = async (
  formId: string
): Promise<{ data: ApiResponse<FormConfig> }> => {
  try {
    const response = await getFormConfigById(`${formId}`);
    return response;
  } catch (error) {
    console.error('Error fetching form configuration by ID:', error);
    throw error;
  }
};

const ProtocolProjectForm: React.FC<ProtocolProjectFormProps> = ({ mode }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formConfig, setFormConfig] = useState<FormConfig | null>(null);
  const [initialValues, setInitialValues] = useState<Record<string, any>>({});
  const navigate = useNavigate();
  const { protocolId, projectId } = useParams<{ protocolId: string; projectId: string }>();
  const location = useLocation();
  const protocolTitle = location.state?.protocolTitle || `协议 #${protocolId}`;
  const isEditMode = mode === 'edit';
  const projectIdNum = isEditMode ? Number(projectId) : undefined;

  const getDefaultFormFields = (): FormField[] => {
    return [
      {
        key: "name",
        label: "项目名称",
        type: "input",
        required: true,
        placeholder: "请输入项目名称",
        prefix: "ProjectOutlined"
      },
      {
        key: "projectCode",
        label: "项目代码",
        type: "input",
        required: true,
        placeholder: "请输入项目代码",
        prefix: "CodeOutlined"
      },
      {
        key: "region",
        label: "区域",
        type: "select",
        required: true,
        placeholder: "请选择区域",
        options: [
          { label: "xinlu", value: "xinlu" },
          { label: "yy", value: "yy" },
          { label: "taizhou", value: "taizhou" },
          { label: "zunyi", value: "zunyi" }
        ]
      },
      {
        key: "appKey",
        label: "AppKey",
        type: "input",
        required: true,
        placeholder: "请输入AppKey",
        prefix: "KeyOutlined",
        tooltip: "应用程序的唯一标识符"
      },
      {
        key: "appSecret",
        label: "AppSecret",
        type: "input",
        required: true,
        placeholder: "请输入AppSecret",
        prefix: "LockOutlined",
        tooltip: "用于验证应用程序身份的密钥"
      },
      {
        key: "token",
        label: "Token",
        type: "input",
        required: false,
        placeholder: "请输入Token",
        prefix: "KeyOutlined",
        tooltip: "访问令牌"
      },
      {
        key: "status",
        label: "状态",
        type: "switch",
        defaultValue: true,
        extra: "启用状态的项目将可以正常使用，禁用状态的项目将无法访问"
      }
    ];
  };

  const getDefaultFormConfig = (): FormConfig => {
    return {
      id: 'default_project_form',
      name: '项目表单',
      fields: getDefaultFormFields()
    };
  };

  useEffect(() => {
    if (isEditMode && projectIdNum) {
      fetchProjectDetails(projectIdNum);
    } else if (!isEditMode && protocolId) {
      form.setFieldsValue({
        area: Number(protocolId),
      });
    }
    
    if (protocolId) {
      fetchFormConfig(Number(protocolId));
    }
  }, [isEditMode, projectIdNum, protocolId, form]);

  const fetchFormConfig = async (protocolId: number) => {
    try {
      
      const formId = `project_${protocolId}`;
      const response = await getFormConfigByIdFetch(formId);
      console.log(response);
      if (response.data.code === 0 && response.data.data) {
        const defaultFields = getDefaultFormFields();
        const dynamicFields = response.data.data.fields || [];
        
        const fieldKeyMap = new Map<string, boolean>();
        defaultFields.forEach(field => fieldKeyMap.set(field.key, true));
        
        const uniqueDynamicFields = dynamicFields.filter((field: FormField) => !fieldKeyMap.has(field.key));
        
        const mergedConfig: FormConfig = {
          ...response.data.data,
          fields: [...defaultFields, ...uniqueDynamicFields]
        };
        
        setFormConfig(mergedConfig);
      } else {
        console.log(`未找到协议 ${protocolId} 的项目表单配置，将使用默认表单`);
        setFormConfig(getDefaultFormConfig());
      }
    } catch (error) {
      console.error('Error fetching form configuration:', error);
      message.error('获取表单配置失败，将使用默认表单');
      setFormConfig(getDefaultFormConfig());
    }
  };

  const fetchProjectDetails = async (id: number) => {
    try {
      setLoading(true);
      const response = await getProjectById(id);
      if (response.data.code === 0 && response.data.data) {
        const projectData = response.data.data;
        
        const values: Record<string, any> = {
          name: projectData.name,
          projectCode: projectData.projectCode,
          region: projectData.region,
          appKey: projectData.appKey,
          appSecret: projectData.appSecret,
          token: projectData.token,
          area: projectData.area,
          status: projectData.status === 1,
        };
        
        if (projectData.exData) {
          Object.keys(projectData.exData).forEach(key => {
            values[key] = projectData.exData?.[key];
          });
        }
        
        setInitialValues(values);
        form.setFieldsValue(values);
      } else {
        message.error(response.data.msg || '获取项目详情失败');
        navigate(`/protocol-projects/${protocolId}`);
      }
    } catch (error) {
      console.error('获取项目详情错误:', error);
      message.error('获取项目详情失败，请检查网络连接后重试');
      navigate(`/protocol-projects/${protocolId}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setSubmitting(true);
      
      const { 
        name, 
        projectCode, 
        region, 
        appKey, 
        appSecret, 
        token,
        status,
        area,
        ...exDataFields 
      } = values;
      
      const projectData = {
        name,
        projectCode,
        region,
        appKey,
        appSecret,
        token,
        status: status ? 1 : 0,
        area: Number(protocolId),
        exData: Object.keys(exDataFields).length > 0 ? exDataFields : undefined
      };
      
      let response;
      
      if (isEditMode && projectIdNum) {
        response = await updateProject(projectIdNum, projectData);
      } else {
        response = await createProject(projectData);
      }
      
      if (response.data.code === 0) {
        message.success(isEditMode ? '项目更新成功' : '项目创建成功');
        navigate(`/protocol-projects/${protocolId}`, { state: { protocolTitle } });
      } else {
        message.error(response.data.msg || (isEditMode ? '更新项目失败' : '创建项目失败'));
      }
    } catch (error) {
      console.error(isEditMode ? '更新项目错误:' : '创建项目错误:', error);
      message.error(isEditMode ? '更新项目失败，请检查网络连接后重试' : '创建项目失败，请检查网络连接后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const renderDynamicFormFields = () => {
    if (!formConfig) return null;
    
    const defaultFieldKeys = getDefaultFormFields().map(field => field.key);
    const dynamicFields = formConfig.fields.filter(field => !defaultFieldKeys.includes(field.key));
    
    if (dynamicFields.length === 0) return null;
    
    return (
      <div className="form-section">
        <Title level={5} style={{ marginBottom: '4px' }}>附加信息</Title>
        <Divider style={{ margin: '0 0 8px' }} />
        
        {dynamicFields.map(field => {
          switch (field.type) {
            case 'input':
              return (
                <Form.Item
                  key={field.key}
                  name={field.key}
                  label={field.label}
                  rules={field.required ? [{ required: true, message: `请输入${field.label}` }] : undefined}
                  style={{ marginBottom: '8px' }}
                >
                  <Input 
                    placeholder={field.placeholder || `请输入${field.label}`} 
                    autoComplete="off"
                  />
                </Form.Item>
              );
            case 'textarea':
              return (
                <Form.Item
                  key={field.key}
                  name={field.key}
                  label={field.label}
                  rules={field.required ? [{ required: true, message: `请输入${field.label}` }] : undefined}
                  style={{ marginBottom: '8px' }}
                >
                  <TextArea 
                    placeholder={field.placeholder || `请输入${field.label}`} 
                    rows={3}
                  />
                </Form.Item>
              );
            case 'select':
              return (
                <Form.Item
                  key={field.key}
                  name={field.key}
                  label={field.label}
                  rules={field.required ? [{ required: true, message: `请选择${field.label}` }] : undefined}
                  style={{ marginBottom: '8px' }}
                >
                  <Select placeholder={field.placeholder || `请选择${field.label}`}>
                    {field.options?.map(option => (
                      <Option key={option.value} value={option.value}>{option.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              );
            case 'switch':
              return (
                <Form.Item
                  key={field.key}
                  name={field.key}
                  label={field.label}
                  valuePropName="checked"
                  extra={field.extra}
                  style={{ marginBottom: '8px' }}
                >
                  <Switch checkedChildren="是" unCheckedChildren="否" />
                </Form.Item>
              );
            default:
              return null;
          }
        })}
      </div>
    );
  };

  const tagColorMap: Record<string, string> = {
    xinlu: 'blue',
    yy: 'green',
    taizhou: 'orange',
    zunyi: 'purple'
  };

  return (
    <div className="protocol-project-form-container">
      <Card className="protocol-project-form-card" bodyStyle={{ padding: '16px' }}>
        <div className="protocol-project-form-header">
          <div>
            <Breadcrumb items={[
              { title: <a onClick={() => navigate('/projects')}>授权协议</a> },
              { title: <a onClick={() => navigate(`/protocol-projects/${protocolId}`, { state: { protocolTitle } })}>{protocolTitle}</a> },
              { title: isEditMode ? '编辑项目' : '创建项目' }
            ]} />
            <Title level={4} style={{ marginTop: '2px', marginBottom: '0', marginLeft: '0' }}>
              {isEditMode ? '编辑项目' : '创建项目'}
            </Title>
            <Text type="secondary" style={{ display: 'block', marginLeft: '0' }}>
              {isEditMode ? '更新项目信息并保存更改' : '填写项目详细信息以创建新项目'}
            </Text>
          </div>
          <Space>

            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={() => navigate(`/protocol-projects/${protocolId}`, { state: { protocolTitle } })}
              className="back-button"
            >
              返回项目列表
            </Button>
          </Space>
        </div>
        
        <Spin spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ status: true, ...initialValues }}
            className="protocol-project-form"
            requiredMark="optional"
            size="middle"
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
              <div style={{ flex: '1', minWidth: '300px' }}>
                <div className="form-section">
                  <Title level={5} style={{ marginBottom: '4px' }}>基本信息</Title>
                  <Divider style={{ margin: '0 0 8px' }} />
                  
                  <Form.Item
                    name="name"
                    label="项目名称"
                    rules={[{ required: true, message: '请输入项目名称' }]}
                    style={{ marginBottom: '8px' }}
                  >
                    <Input 
                      placeholder="请输入项目名称" 
                      prefix={<ProjectOutlined className="form-icon" />} 
                      autoComplete="off"
                    />
                  </Form.Item>
                  
                  <Form.Item
                    name="projectCode"
                    label="项目代码"
                    rules={[{ required: false, message: '请输入项目代码' }]}
                    style={{ marginBottom: '8px' }}
                  >
                    <Input 
                      placeholder="请输入项目代码" 
                      prefix={<CodeOutlined className="form-icon" />}
                      autoComplete="off"
                    />
                  </Form.Item>
                  
                  <Form.Item
                    name="region"
                    label="区域"
                    rules={[{ required: true, message: '请选择区域' }]}
                    style={{ marginBottom: '8px' }}
                  >
                    <Select
                      placeholder="请选择区域"
                      suffixIcon={<GlobalOutlined className="form-icon" />}
                      className="region-select"
                      dropdownRender={(menu) => (
                        <div>
                          {menu}
                        </div>
                      )}
                      optionLabelProp="label"
                      optionRender={(option) => (
                        <Tag color={tagColorMap[option.value as string]}>{option.value}</Tag>
                      )}
                      tagRender={({ label, value, closable, onClose }) => (
                        <Tag color={tagColorMap[value as string]} style={{ marginRight: 3 }}>
                          {label}
                        </Tag>
                      )}
                    >
                      <Option value="xinlu" label="xinlu">
                        <Tag color={tagColorMap.xinlu}>xinlu</Tag>
                      </Option>
                      <Option value="yy" label="yy">
                        <Tag color={tagColorMap.yy}>yy</Tag>
                      </Option>
                      <Option value="taizhou" label="taizhou">
                        <Tag color={tagColorMap.taizhou}>taizhou</Tag>
                      </Option>
                      <Option value="zunyi" label="zunyi">
                        <Tag color={tagColorMap.zunyi}>zunyi</Tag>
                      </Option>
                    </Select>
                  </Form.Item>
                </div>
                
                <div className="form-section">
                  <Title level={5} style={{ marginBottom: '4px' }}>项目状态</Title>
                  <Divider style={{ margin: '0 0 8px' }} />
                  
                  <Form.Item
                    name="status"
                    label="状态"
                    valuePropName="checked"
                    extra="启用状态的项目将可以正常使用，禁用状态的项目将无法访问"
                    style={{ marginBottom: '8px' }}
                  >
                    <Switch checkedChildren="启用" unCheckedChildren="禁用" />
                  </Form.Item>
                </div>
              </div>
              
              <div style={{ flex: '1', minWidth: '300px' }}>
                <div className="form-section">
                  <Title level={5} style={{ marginBottom: '4px' }}>认证信息</Title>
                  <Divider style={{ margin: '0 0 8px' }} />
                  
                  <Form.Item
                    name="appKey"
                    label={
                      <span>
                        AppKey
                        <Tooltip title="应用程序的唯一标识符">
                          <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                        </Tooltip>
                      </span>
                    }
                    rules={[{ required: false, message: '请输入AppKey' }]}
                    style={{ marginBottom: '8px' }}
                  >
                    <Input 
                      placeholder="请输入AppKey" 
                      prefix={<KeyOutlined className="form-icon" />}
                      autoComplete="off"
                    />
                  </Form.Item>
                  
                  <Form.Item
                    name="appSecret"
                    label={
                      <span>
                        AppSecret
                        <Tooltip title="用于验证应用程序身份的密钥">
                          <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                        </Tooltip>
                      </span>
                    }
                    rules={[{ required: false, message: '请输入AppSecret' }]}
                    style={{ marginBottom: '8px' }}
                  >
                    <Input 
                      placeholder="请输入AppSecret" 
                      prefix={<LockOutlined className="form-icon" />}
                      autoComplete="off"
                    />
                  </Form.Item>
                  
                  <Form.Item
                    name="token"
                    label={
                      <span>
                        Token
                        <Tooltip title="访问令牌">
                          <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                        </Tooltip>
                      </span>
                    }
                    rules={[{ required: false, message: '请输入Token' }]}
                    style={{ marginBottom: '8px' }}
                  >
                    <Input 
                      placeholder="请输入Token" 
                      prefix={<KeyOutlined className="form-icon" />}
                      autoComplete="off"
                    />
                  </Form.Item>
                </div>
                
                {renderDynamicFormFields()}
              </div>
            </div>
            
            <Form.Item
              name="area"
              hidden
            >
              <Input type="hidden" />
            </Form.Item>
            
            <Form.Item className="form-actions" style={{ marginBottom: '0', marginTop: '12px' }}>
              <Space>
                <Button 
                  onClick={() => navigate(`/protocol-projects/${protocolId}`, { state: { protocolTitle } })}
                >
                  取消
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={submitting}
                  icon={<SaveOutlined />}
                >
                  {isEditMode ? '更新' : '创建'}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Card>
    </div>
  );
};

export default ProtocolProjectForm; 